import { MoreHorizontal, Send, Sparkles } from 'lucide-react'
import { Button } from '../common/Button'
import { Input } from '../common/forms/Controls'
import { IconButton } from '../common/primitives/IconButton'
import { Menu } from '../common/overlays/Menu'
import styles from './AssistBar.module.css'

function resultText(result) {
  if (typeof result === 'string') return result
  return result?.text || result?.value || ''
}

// An optional short tag shown beside a result (for example the style it was written in).
function resultLabel(result) {
  if (typeof result === 'string') return ''
  return result?.label || ''
}

export function AssistBar({
  id,
  label = 'AI writing assistance',
  prompt = '',
  promptLabel = 'Describe the edit',
  placeholder = 'Describe edits',
  scope,
  actions = [],
  overflowActions = [],
  onPromptChange,
  onRequest,
  pending = false,
  disabled = false,
  pendingLabel = 'Generating suggestions',
  results = [],
  resultsLabel = 'AI suggestions',
  onApplyResult,
  applyLabel = 'Use',
  note,
  className = '',
}) {
  const submitPrompt = () => {
    const instruction = String(prompt || '').trim()
    if (!instruction || pending || disabled) return
    onRequest?.(instruction, { source: 'prompt' })
  }

  const requestAction = (action) => {
    if (pending || disabled || action.disabled) return
    onRequest?.(action.instruction || action.label, { source: 'action', action })
  }

  const menuItems = overflowActions.map((action) => ({
    ...action,
    onSelect: () => requestAction(action),
    disabled: disabled || pending || action.disabled,
  }))

  return (
    <div id={id} className={`${styles.container} ${className}`.trim()}>
      <div className={styles.bar} role="group" aria-label={label} aria-busy={pending || undefined}>
        <span className={styles.spark} aria-hidden="true"><Sparkles size={15} /></span>
        {scope ? <span className={styles.scope}>{scope}</span> : null}
        <Input
          size="compact"
          className={styles.prompt}
          aria-label={promptLabel}
          placeholder={placeholder}
          value={prompt}
          disabled={disabled || pending}
          onChange={(event) => onPromptChange?.(event.target.value)}
          onKeyDown={(event) => {
            if (event.key !== 'Enter' || event.nativeEvent?.isComposing) return
            event.preventDefault()
            event.stopPropagation()
            submitPrompt()
          }}
        />
        <span className={styles.actions}>
          {actions.map((action) => (
            <Button
              key={action.key || action.label}
              type="button"
              size="compact"
              variant="ghost"
              disabled={disabled || pending || action.disabled}
              onClick={() => requestAction(action)}
            >
              {action.icon ? <action.icon size={14} aria-hidden="true" /> : null}
              {action.label}
            </Button>
          ))}
          {menuItems.length ? (
            <Menu
              trigger={<IconButton label="More AI actions" variant="ghost" disabled={disabled || pending}><MoreHorizontal size={15} aria-hidden="true" /></IconButton>}
              triggerLabel="More AI actions"
              items={menuItems}
            />
          ) : null}
          <IconButton
            label="Send edit instruction"
            variant="ghost"
            disabled={disabled || pending || !String(prompt || '').trim()}
            onClick={submitPrompt}
          >
            <Send size={15} aria-hidden="true" />
          </IconButton>
        </span>
      </div>

      <span className={styles.announcement} role="status" aria-live="polite" aria-atomic="true">
        {pending ? pendingLabel : results.length ? `${results.length} ${results.length === 1 ? 'suggestion' : 'suggestions'} ready` : ''}
      </span>

      {results.length ? (
        <div className={styles.results} aria-label={resultsLabel}>
          {results.map((result, index) => {
            const text = resultText(result)
            const label = resultLabel(result)
            return (
              <div key={result?.id || `${text}-${index}`} className={`${styles.result} ${label ? styles.resultLabeled : ''}`.trim()}>
                <p>{text}</p>
                {label ? <span className={styles.resultLabel}>{label}</span> : null}
                {onApplyResult ? (
                  <Button type="button" size="compact" variant="secondary" onClick={() => onApplyResult(text, result)}>
                    {applyLabel}
                  </Button>
                ) : null}
              </div>
            )
          })}
        </div>
      ) : null}

      {note && results.length ? <p className={styles.note}>{note}</p> : null}
    </div>
  )
}

export default AssistBar
